import { type FC, type ReactNode, useEffect, useState } from "react";
import OrderDetailsSummary from "~/features/orders/orderDetails/components/OrderDetailsSummary";
import OrderDetails from "~/features/orders/orderDetails/components/OrderDetails/OrderDetails";
import Spinner from "~/common/components/Spinner/Spinner";
import { orderDetailsMounted } from "~/features/cart/cart/redux/actions/orderDetailsActions";
import { useDispatch } from "react-redux";
import { addBaseProductDataAction } from "~/domains/product/redux/actions";
import ShipmentDetails from "~/features/orders/orderDetails/components/ShipmentDetails/ShipmentDetails";
import { Stack, Tab, TabContext, Tabs, Typography, styled } from "~/common/mui";
import { fetchOrderDetails } from "~/features/orders/orderList/api/fetchOrderDetails/fetchOrderDetails";
import { type DetailedOrderData } from "~/domains/orders/types/modelsV2";
import { useTranslation } from "react-i18next";
import { type CaseDataObject } from "~/features/orders/orderDetails/components/ServiceCases/types";
import { fetchServiceCases } from "~/features/orders/orderDetails/components/ServiceCases/api";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import {
  type FeedbackOptionCategory,
  OrderListFeedbackContextProvider,
  type UserData,
} from "~/features/orders/orderDetails/hooks/useSendOrderFeedback/components/OrderListFeedbackContext";
import { Alert } from "~/common/mui/Alert/Alert";
import OrderDetailsHeaderV2 from "../orderDetailsV2/OrderDetailsV2/OrderDetailsHeaderV2";
import OrderDetailsSummaryV2 from "../orderDetailsV2/OrderDetailsV2/OrderDetailsSummaryV2";
import { OrderListDesignToggleProvider, useDesignToggle } from "../common/components/NewDesignToggle";
import ShipmentDetailsV2 from "../orderDetailsV2/ShipmentDetailsV2/ShipmentDetailsV2";
import { C_OUTLINE_VARIANT } from "~/common/components/icon/Colors";
import OrderDetailsV2 from "../orderDetailsV2/OrderDetailsV2/OrderDetailsV2";
import { StyledTabPanel } from "../common/components/v2/OrderAndQuoteSharedStyles";
import { orderPageStyles } from "~/features/orders/orderStyles";

const OrderDetailsStyleScope = styled("div")(({ theme }) => orderPageStyles(theme));

export function createOrderDetailsPage(data: { userData: UserData; orderFeedbackOptions: FeedbackOptionCategory[] }) {
  const params = new URLSearchParams(window.location.search);
  const soNumber = params.get("id") || undefined;

  return (
    <OrderListFeedbackContextProvider value={data}>
      <OrderListDesignToggleProvider>
        <OrderDetailsPage soNumber={soNumber} />
      </OrderListDesignToggleProvider>
    </OrderListFeedbackContextProvider>
  );
}

interface Props {
  soNumber?: string;
}

interface OrderDetailsDisplayState {
  loading: boolean;
  error: boolean;
  data?: DetailedOrderData;
  serviceCases?: Record<string, CaseDataObject[]>;
}

const OrderDetailsPage: FC<Props> = ({ soNumber }: Props) => {
  const { isNewDesignEnabled } = useDesignToggle();
  const [state, setState] = useState<OrderDetailsDisplayState>({
    loading: true,
    error: false,
  });

  const [activeTabIndex, setActiveTab] = useState<string>(TabId.ORDER_DETAILS);

  const missingId = !soNumber;
  const dispatch = useDispatch();

  useEffect(() => {
    if (missingId) {
      setState({
        loading: false,
        error: false,
      });
    } else {
      (async () => {
        try {
          let data;
          let serviceCases = {};
          if (userHasPermission(Permission.CAN_SEE_SERVICE_CASES)) {
            [data, serviceCases] = await Promise.all([fetchOrderDetails(soNumber!), fetchServiceCases(soNumber)]);
          } else {
            data = await fetchOrderDetails(soNumber!);
          }
          dispatch(addBaseProductDataAction(data.products.map((entry) => entry.product)));
          setState({
            data,
            serviceCases,
            loading: false,
            error: false,
          });
        } catch {
          setState({
            loading: false,
            error: true,
          });
        }
      })();
    }
  }, [soNumber]);

  useEffect(() => {
    dispatch(orderDetailsMounted());
  }, []);

  const { t } = useTranslation();
  const { loading, error } = state;

  let content: ReactNode | null = null;

  if (missingId) {
    const msg = t("orderDetailsPage.error.missingOrder");
    content = (
      <Alert className="mt-3" severity="error">
        {msg}
      </Alert>
    );
  } else if (loading) {
    content = <Spinner className="mt-3" />;
  } else if (error) {
    const msg = t("orderDetailsPage.error.general");
    content = (
      <Alert className="mt-3" severity="error">
        {msg}
      </Alert>
    );
  } else {
    const data = state.data!;
    const serviceCases = state.serviceCases;
    const hasDeliveryDetails = data.deliveryEntries && data.deliveryEntries.length > 0;
    const shipmentDetails = <ShipmentDetails data={data} />;
    const orderDetails = <OrderDetails data={data} serviceCases={serviceCases} />;
    const shipmentDetailsV2 = <ShipmentDetailsV2 data={data} />;
    const orderDetailsV2 = <OrderDetailsV2 data={data} serviceCases={serviceCases} />;

    content = (
      <>
        <Stack gap={3}>
          {!isNewDesignEnabled && <OrderDetailsSummary data={data} />}
          {!hasDeliveryDetails && !isNewDesignEnabled && orderDetails}

          {isNewDesignEnabled && (
            <>
              <OrderDetailsHeaderV2 data={data} />
              <OrderDetailsSummaryV2 data={data} />
            </>
          )}
          <Stack>{!hasDeliveryDetails && isNewDesignEnabled && orderDetailsV2}</Stack>

          {hasDeliveryDetails && (
            <>
              <TabContext value={activeTabIndex + ""}>
                <Tabs
                  id="tabs"
                  onChange={(e, val) => {
                    setActiveTab(val);
                  }}
                  value={activeTabIndex}
                  fullWidthIndicator={false}
                  variant={"fullWidth"}
                >
                  <Tab value={TabId.ORDER_DETAILS} label={t("orderDetailsPage.title")} />
                  <Tab value={TabId.SHIPMENT_DETAILS} label={t("orderDetailsPage.shipmentDetails.title")} />
                </Tabs>

                <StyledTabPanel value={TabId.ORDER_DETAILS} keepMounted>
                  {!isNewDesignEnabled && orderDetails}
                  {isNewDesignEnabled && orderDetailsV2}
                </StyledTabPanel>

                <StyledTabPanel value={TabId.SHIPMENT_DETAILS} keepMounted>
                  {!isNewDesignEnabled && shipmentDetails}
                  {isNewDesignEnabled && shipmentDetailsV2}
                </StyledTabPanel>
              </TabContext>
            </>
          )}
        </Stack>
      </>
    );
  }

  return (
    <OrderDetailsStyleScope>
      <Stack>
        {!isNewDesignEnabled && (
          <Typography
            variant="headlineMedium"
            sx={{
              borderBottom: `1px solid ${C_OUTLINE_VARIANT}`,
              marginTop: "5px",
              marginBottom: "30px",
            }}
          >
            {t("orderDetailsPage.title")}
          </Typography>
        )}

        {content}
      </Stack>
    </OrderDetailsStyleScope>
  );
};

export default OrderDetailsPage;

enum TabId {
  ORDER_DETAILS = "Order Details",
  SHIPMENT_DETAILS = "Shipment Details",
}
